/**
 * User profile menu component in header
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useGuardedNavigate } from '../../shared/hooks/useGuardedNavigate';
import { useQuery } from '@tanstack/react-query';
import { LogOut, Settings, Sun, Moon, Monitor, UserPlus, Languages, Check, Globe, Code2, Mail } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { useAuth, hasDeveloperPortalAccess } from '../../shared/auth';
import { API_BASE_URL, needsWorkspaceSelector } from '../../shared/api/config';
import { customInstance } from '../../shared/api/axiosInstance';
import { clearStoredWorkspaceDomain } from '../../shared/utils/workspaceDomain';
import { useThemeMode, type ThemeMode } from '../../shared/contexts/ThemeContext';
import { useGetSummaryApiV1MyCorrespondenceSummaryGet } from '../../generated/api/my-correspondence/my-correspondence';
import { SUPPORTED_LANGUAGES, getLanguageDirection, changeLanguage, persistKnownLanguage } from '../../i18n';
import type { SupportedLanguage } from '../../shared/auth/types';
import { getErrorMessage } from '../../shared/utils/errors';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';
import { InviteUserDialog } from './InviteUserDialog';
import { clearRefreshToken } from '../../shared/auth/secureTokenStore';
import { AUTH_STORAGE_KEYS } from '../../shared/auth/storageKeys';

export function UserMenu() {
  // TWO navigators on purpose. The menu ENTRIES are destinations the user picked,
  // so they go through the guard like any other chrome navigation. The post-logout
  // hop below must NOT: the session is already gone by then, so a "Stay" would
  // strand the user on an authenticated page with no way to save anything.
  const navigateGuarded = useGuardedNavigate();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation(['common', 'user_management']);
  const { user, logout, updateUser } = useAuth();
  const { themeMode, setThemeMode } = useThemeMode();
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);

  // Check if user can invite others
  const { data: canInviteData } = useQuery({
    queryKey: ['can-invite'],
    queryFn: () =>
      customInstance<{ can_invite: boolean }>({
        url: '/api/v1/user-management/settings/can-invite',
        method: 'GET',
      }),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const canInvite = canInviteData?.can_invite ?? false;

  // "My Correspondence" only appears for users who actually have cross-tenant
  // correspondence addressed to their verified email (resolver returns ≥1).
  const { data: correspondenceSummary } =
    useGetSummaryApiV1MyCorrespondenceSummaryGet({
      query: {
        staleTime: 5 * 60 * 1000,
        // A background nav-visibility probe: `UserMenu` is mounted on EVERY page,
        // nobody asks for this read, and the whole cost of a failure is one menu
        // entry that stays hidden. Its endpoint shares the cross-tenant
        // correspondent-read limiter (30/min per USER, counted across every open
        // tab and device), so the realistic failure is a 429 — and a toast reading
        // "Rate limit exceeded. Please try again later." at someone idling on an
        // unrelated page is noise they cannot act on. The storm itself is fixed at
        // source (`my-correspondence` no longer rides `user_management` cues, see
        // `shared/data-freshness/moduleFromQueryKey.ts`); this keeps the remaining
        // failure modes silent, as befits a probe the user never initiated.
        meta: { suppressGlobalErrorToast: true },
      },
    });
  const hasCorrespondence =
    (correspondenceSummary?.ticket_count ?? 0) > 0 ||
    (correspondenceSummary?.bill_count ?? 0) > 0;

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleThemeToggle = () => {
    const nextMode: Record<ThemeMode, ThemeMode> = {
      light: 'dark',
      dark: 'system',
      system: 'light',
    };
    setThemeMode(nextMode[themeMode]);
  };

  const getThemeIcon = () => {
    switch (themeMode) {
      case 'light':
        return <Sun className="h-4 w-4" />;
      case 'dark':
        return <Moon className="h-4 w-4" />;
      case 'system':
        return <Monitor className="h-4 w-4" />;
    }
  };

  const getThemeLabel = () => {
    switch (themeMode) {
      case 'light':
        return t('theme.light');
      case 'dark':
        return t('theme.dark');
      case 'system':
        return t('theme.system');
    }
  };

  const currentLanguage = SUPPORTED_LANGUAGES.find(
    l => l.code === i18n.language
  ) || SUPPORTED_LANGUAGES[0];

  const handleLanguageSelect = async (code: string) => {
    await changeLanguage(code);
    const direction = getLanguageDirection(code);
    document.documentElement.dir = direction;
    document.documentElement.lang = code;
    // Cross-subdomain cookie + localStorage so the choice follows the user across
    // `*.panluma.ai` subdomains (and the SSO-callback host) immediately.
    persistKnownLanguage(code);
    // Persist as the durable account preference so it survives across devices
    // and is re-applied on the next login, overriding GeoIP. See
    // useApplyUserLanguage. The local switch above already took effect; a failed
    // save only means it won't follow the user to another device.
    if (user && user.preferred_language !== code) {
      try {
        await updateUser({ preferred_language: code as SupportedLanguage });
      } catch (error) {
        // eslint-disable-next-line no-restricted-syntax -- bare non-mutation call, no global handler fires
        toast.error(getErrorMessage(error));
      }
    }
  };

  if (!user) {
    return null;
  }

  // Get initials for avatar fallback
  const initials = `${user.first_name.charAt(0)}${user.last_name.charAt(0)}`.toUpperCase();

  // Build full profile image URL if available
  const profileImageUrl = user.profile_image_url
    ? `${API_BASE_URL}${user.profile_image_url}`
    : undefined;

  return (
    // shrink-0: the profile menu is a critical app-bar item — it must never be
    // compressed or clipped out of the mobile header by crowding siblings.
    <div className="flex items-center gap-2 shrink-0">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className="relative h-9 w-9 rounded-full" data-testid="user-menu">
            <Avatar className="h-9 w-9">
              <AvatarImage src={profileImageUrl} alt={user.full_name} />
              <AvatarFallback className="bg-primary text-primary-foreground">
                {initials}
              </AvatarFallback>
            </Avatar>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <div className="px-2 py-1.5">
            <p className="text-sm font-semibold">{user.full_name}</p>
            <p className="text-xs text-muted-foreground">{user.email}</p>
          </div>
          <DropdownMenuSeparator />
          <DropdownMenuSub>
            <DropdownMenuSubTrigger data-testid="language-menu">
              <Languages className="h-4 w-4" />
              <span className="ms-2">
                {t('common:userMenu.language')}: {currentLanguage.nativeName}
              </span>
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent className="min-w-[180px]">
              {SUPPORTED_LANGUAGES.map((lang) => {
                // The endonym is the primary line; `translatedName` is the same
                // language named in the READER's language. They are equal for a
                // reader viewing their own language, and then there is no second
                // line to show.
                const translatedName = t(`common:languages.${lang.code}`);
                return (
                  <DropdownMenuItem
                    key={lang.code}
                    data-testid={`language-option-${lang.code}`}
                    onClick={() => handleLanguageSelect(lang.code)}
                    className={cn(
                      'flex items-center justify-between',
                      lang.code === currentLanguage.code && 'bg-accent'
                    )}
                  >
                    <div className="flex flex-col">
                      <span>{lang.nativeName}</span>
                      {translatedName !== lang.nativeName && (
                        <span className="text-xs text-muted-foreground">{translatedName}</span>
                      )}
                    </div>
                    {lang.code === currentLanguage.code && (
                      <Check className="h-4 w-4 text-primary" />
                    )}
                  </DropdownMenuItem>
                );
              })}
            </DropdownMenuSubContent>
          </DropdownMenuSub>
          <DropdownMenuItem
            onClick={handleThemeToggle}
            onSelect={(e) => e.preventDefault()}
          >
            {getThemeIcon()}
            <span className="ms-2">
              {t('common:theme.label')}: {getThemeLabel()}
            </span>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => navigateGuarded('/settings')}>
            <Settings className="h-4 w-4" />
            <span className="ms-2">{t('common:userMenu.settings')}</span>
          </DropdownMenuItem>
          {hasCorrespondence && (
            <DropdownMenuItem
              onClick={() => navigateGuarded('/correspondence')}
              data-testid="my-correspondence-menu-item"
            >
              <Mail className="h-4 w-4" />
              <span className="ms-2">
                {t('user_management:correspondence.menuItem')}
              </span>
            </DropdownMenuItem>
          )}
          {hasDeveloperPortalAccess(user) && (
            <DropdownMenuItem onClick={() => navigateGuarded('/developer')} data-testid="developer-portal-menu-item">
              <Code2 className="h-4 w-4" />
              <span className="ms-2">{t('common:userMenu.developerPortal')}</span>
            </DropdownMenuItem>
          )}
          {canInvite && (
            <DropdownMenuItem onClick={() => setInviteDialogOpen(true)}>
              <UserPlus className="h-4 w-4" />
              <span className="ms-2">{t('user_management:inviteUser.menuItem')}</span>
            </DropdownMenuItem>
          )}
          <div className="px-2 py-1.5 flex items-center gap-1 text-xs text-muted-foreground">
            <a href="https://www.panluma.ai/privacy-policy" target="_blank" rel="noopener noreferrer" className="hover:text-foreground hover:underline">
              {t('common:userMenu.privacyPolicy')}
            </a>
            <span>·</span>
            <a href="https://www.panluma.ai/terms-of-service" target="_blank" rel="noopener noreferrer" className="hover:text-foreground hover:underline">
              {t('common:userMenu.termsOfService')}
            </a>
          </div>
          <DropdownMenuSeparator />
          {needsWorkspaceSelector && (
            <DropdownMenuItem
              onClick={() => {
                // Awaited before the reload, deliberately. The Keychain is
                // per-APP, not per-workspace-domain, so a token left behind
                // here would be exchanged against the NEXT workspace's API
                // base — and a reload that raced the delete would leave it
                // there permanently, since the reload kills the JS context
                // this delete is running in.
                void (async () => {
                  clearStoredWorkspaceDomain();
                  localStorage.removeItem(AUTH_STORAGE_KEYS.accessToken);
                  localStorage.removeItem(AUTH_STORAGE_KEYS.user);
                  await clearRefreshToken();
                  window.location.reload();
                })();
              }}
              data-testid="switch-workspace-button"
            >
              <Globe className="h-4 w-4" />
              <span className="ms-2">{t('auth:workspace.switchWorkspace')}</span>
            </DropdownMenuItem>
          )}
          <DropdownMenuItem onClick={handleLogout} data-testid="logout-button">
            <LogOut className="h-4 w-4" />
            <span className="ms-2">{t('common:userMenu.logout')}</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <InviteUserDialog open={inviteDialogOpen} onOpenChange={setInviteDialogOpen} />
    </div>
  );
}
